import React from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import { useDailySales, useWeekToDateSales, useMonthToDateSales, useYearToDateSales, useTrailing12Months } from '../hooks/useSalesData';
import { format } from 'date-fns';

interface ComprehensiveMetricsProps {
  selectedLocation?: string;
}

interface MetricCardProps {
  title: string;
  current: number;
  lastYear: number;
  percentChange: number;
  prefix?: string;
  isLoading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  current,
  lastYear,
  percentChange,
  prefix = '$',
  isLoading = false,
}) => {
  const theme = useTheme();
  const isPositive = percentChange >= 0;

  if (isLoading) {
    return (
      <Card sx={{ ...glassStyle, p: 3, height: '100%' }}>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="80%" height={40} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
      </Card>
    );
  }

  return (
    <Card
      sx={{
        ...glassStyle,
        p: 3,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 32px rgba(175, 35, 38, 0.2)',
        },
      }}
    >
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        {prefix}{current.toLocaleString()}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {isPositive ? (
          <TrendingUpIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
        ) : (
          <TrendingDownIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />
        )}
        <Typography
          variant="body2"
          sx={{
            color: isPositive ? theme.palette.success.main : theme.palette.error.main,
            fontWeight: 600,
          }}
        >
          {Math.abs(percentChange).toFixed(1)}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          vs last year
        </Typography>
      </Box>
      
      <Typography variant="caption" color="text.secondary">
        Last Year: {prefix}{lastYear.toLocaleString()}
      </Typography>
      
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 60,
          height: 60,
          opacity: 0.1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CalendarIcon sx={{ fontSize: 48 }} />
      </Box>
    </Card>
  );
};

const ComprehensiveMetrics: React.FC<ComprehensiveMetricsProps> = ({ selectedLocation }) => {
  const theme = useTheme();
  const today = format(new Date(), 'yyyy-MM-dd');
  const storeId = selectedLocation === 'all' ? undefined : selectedLocation;

  // Fetch all metrics
  const { data: dailySales, isLoading: isDailyLoading } = useDailySales(today, storeId);
  const { data: wtdSales, isLoading: isWtdLoading } = useWeekToDateSales(undefined, storeId);
  const { data: mtdSales, isLoading: isMtdLoading } = useMonthToDateSales(undefined, storeId);
  const { data: ytdSales, isLoading: isYtdLoading } = useYearToDateSales(undefined, storeId);
  const { data: trailing12, isLoading: isTrailingLoading } = useTrailing12Months(undefined, storeId);

  return (
    <Box>
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Comprehensive YoY Metrics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete view of year-over-year performance across all time periods
          </Typography>
        </Box>
      </motion.div>

      <Grid container spacing={3}>
        {/* Daily YoY */}
        <Grid item xs={12} sm={6} md={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <MetricCard
              title="Daily Sales (YoY)"
              current={dailySales?.current?.totalSales || 0}
              lastYear={dailySales?.comparisons?.yearOverYear?.lastYearData?.totalSales || 0}
              percentChange={dailySales?.comparisons?.yearOverYear?.salesPercent || 0}
              isLoading={isDailyLoading}
            />
          </motion.div>
        </Grid>

        {/* WTD YoY */}
        <Grid item xs={12} sm={6} md={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.2 }}>
            <MetricCard
              title="Week-to-Date (YoY)"
              current={wtdSales?.current?.totalSales || 0}
              lastYear={wtdSales?.lastYear?.totalSales || 0}
              percentChange={wtdSales?.yearOverYear?.salesPercent || 0}
              isLoading={isWtdLoading}
            />
          </motion.div>
        </Grid>

        {/* MTD YoY */}
        <Grid item xs={12} sm={6} md={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.3 }}>
            <MetricCard
              title="Month-to-Date (YoY)"
              current={mtdSales?.current?.totalSales || 0}
              lastYear={mtdSales?.lastYear?.totalSales || 0}
              percentChange={mtdSales?.yearOverYear?.salesPercent || 0}
              isLoading={isMtdLoading}
            />
          </motion.div>
        </Grid>

        {/* YTD YoY */}
        <Grid item xs={12} sm={6} md={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <MetricCard
              title="Year-to-Date (YoY)"
              current={ytdSales?.current?.totalSales || 0}
              lastYear={ytdSales?.lastYear?.totalSales || 0}
              percentChange={ytdSales?.yearOverYear?.salesPercent || 0}
              isLoading={isYtdLoading}
            />
          </motion.div>
        </Grid>

        {/* Trailing 12 Months */}
        <Grid item xs={12} sm={6} md={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.5 }}>
            <MetricCard
              title="Trailing 12 Months"
              current={trailing12?.summary?.current?.totalSales || 0}
              lastYear={trailing12?.summary?.lastYear?.totalSales || 0}
              percentChange={trailing12?.summary?.yearOverYear?.salesPercent || 0}
              isLoading={isTrailingLoading}
            />
          </motion.div>
        </Grid>

        {/* Performance Score */}
        <Grid item xs={12} sm={6} md={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.6 }}>
            <Card sx={{ ...glassStyle, p: 3, height: '100%' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Overall Performance Score
              </Typography>
              <Box sx={{ mt: 2, mb: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {calculatePerformanceScore(
                    dailySales?.comparisons?.yearOverYear?.salesPercent,
                    wtdSales?.yearOverYear?.salesPercent,
                    mtdSales?.yearOverYear?.salesPercent,
                    ytdSales?.yearOverYear?.salesPercent
                  )}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={calculatePerformanceScore(
                  dailySales?.comparisons?.yearOverYear?.salesPercent,
                  wtdSales?.yearOverYear?.salesPercent,
                  mtdSales?.yearOverYear?.salesPercent,
                  ytdSales?.yearOverYear?.salesPercent
                )}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #af2326 0%, #c94144 100%)',
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Poor
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Excellent
                </Typography>
              </Box>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Monthly Breakdown for Trailing 12 Months */}
      {trailing12?.monthlyBreakdown && (
        <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.7 }}>
          <Card sx={{ ...glassStyle, p: 3, mt: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Monthly YoY Comparison (Last 12 Months)
            </Typography>
            <Grid container spacing={2}>
              {trailing12.monthlyBreakdown.slice(-6).map((month: any, index: number) => {
                if (!month) return null;
                const salesPercent = month?.yearOverYear?.salesPercent || 0;
                const totalSales = month?.current?.totalSales || 0;
                
                return (
                  <Grid item xs={12} sm={6} md={2} key={month?.month || index}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {month?.month || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                        ${(totalSales / 1000).toFixed(1)}K
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        {salesPercent >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: salesPercent >= 0
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 600,
                          }}
                        >
                          {Math.abs(salesPercent).toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Card>
        </motion.div>
      )}
    </Box>
  );
};

// Helper function to calculate performance score
function calculatePerformanceScore(...percentages: (number | undefined)[]): number {
  const validPercentages = percentages.filter(p => p !== undefined) as number[];
  if (validPercentages.length === 0) return 50;
  
  const avgPercentage = validPercentages.reduce((sum, p) => sum + p, 0) / validPercentages.length;
  // Convert percentage to 0-100 score, where 0% growth = 50 score
  return Math.min(100, Math.max(0, 50 + (avgPercentage * 2)));
}

export default ComprehensiveMetrics;